import React from 'react';
import { FaAngleDown, FaAngleUp, FaRegStickyNote } from 'react-icons/fa';
import { Link } from 'react-router-dom';

import imageCalendar from '../assets/calendar-font-awesome@3x.png';
import imageSpaceShip from '../assets/rocket-font-awesome@3x.png';
import temperatureIcon from '../../../components/icons/temperatureIcon.svg';
import porterbuddyOrderIcon from '../assets/porterbuddy-order.png';
import instaboxOrderIcon from '../assets/instabox-order.png';

import { COLOR } from '../../../const/color';
import {
  AddressValidity,
  OrderDeliveryType,
  OrderListOrderClazz,
  OrderPackageSize,
  OrderStatus,
  Region,
  ServiceBrand,
} from '../../../types/orderTypes';
import { Loader } from '../../../components/Loader/Loader';
import { classNames } from '../../../utils/classUtils';
import { regionSupportsTemperatureControl } from '../../../const/HubConfig';

const alertStatus: {
  [status in OrderStatus]: { name: string; type: string };
} = {
  accepted: { name: 'Accepted', type: 'primary' },
  in_delivery: { name: 'In delivery', type: 'primary' },
  delivered: { name: 'Delivered', type: 'success' },
  canceled: { name: 'Canceled', type: 'danger' },
  ready: { name: 'Ready', type: 'primary' },
};

function parcelCount(order: OrderListOrderClazz): string {
  if (!order) {
    return '';
  }

  if (order.packagesNumber === 1) {
    return '(1 parcel)';
  }

  return `(${order.packagesNumber} parcels)`;
}

function packageSizeAsText(order: OrderListOrderClazz): string {
  if (order.xxl) {
    return 'XXL';
  }
  switch (order.packageSize) {
    case OrderPackageSize.Small:
      return 'Small';
    case OrderPackageSize.Medium:
      return 'Medium';
    case OrderPackageSize.Large:
      return 'Large';
    case OrderPackageSize.Xxl:
      return 'XXL';
    default:
      return '-';
  }
}

export default function OrderListLine({
  order,
  bundleId,
  fetchingBundle,
  onShowMore,
  showMore,
  onSelected,
  selected,
  region,
}: {
  order: OrderListOrderClazz;
  bundleId?: string;
  fetchingBundle: boolean;
  onShowMore: () => void;
  showMore: boolean;
  onSelected?: () => void;
  selected: boolean;
  region: Region;
}): JSX.Element {
  const express = order.deliveryType === OrderDeliveryType.Express;
  const hasNotes =
    order && order.noteForCourier && order.noteForCourier.trim().length > 1;

  return (
    <div
      className={classNames(
        {
          'order-list-item__selected': selected,
        },
        'order-list-item'
      )}
    >
      <div className="order-information" onClick={() => onShowMore()}>
        <div
          style={{ display: 'flex', alignItems: 'center', paddingLeft: '5px' }}
        >
          {onSelected && (
            <label
              className="order-list-item__checkbox"
              htmlFor={`order-list-item${order.id}`}
              onClick={e => e.stopPropagation()}
            >
              <input
                type="checkbox"
                id={`order-list-item${order.id}`}
                onChange={() => onSelected()}
                checked={!!selected}
              />
            </label>
          )}
          <div>
            <Link to={`/orders/${order.id}`} className="link__clean">
              {order.id}
            </Link>
            <br />
            {!fetchingBundle && bundleId && (
              <Link to={`/bundles/${bundleId}`} className="link__clean">
                {bundleId}
              </Link>
            )}
            {fetchingBundle && <Loader type="bounce" />}
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <div className="order-list-item__icons">
              <img
                src={express ? imageSpaceShip : imageCalendar}
                alt={express ? 'Express' : 'Scheduled'}
                style={{ height: 20, gridArea: 'type' }}
              />
              {hasNotes && (
                <span
                  title={order.noteForCourier}
                  style={{ height: 20, gridArea: 'note' }}
                >
                  <FaRegStickyNote
                    style={{
                      width: 20,
                      height: 20,
                      color: '#3a3d4b',
                      cursor: 'pointer',
                    }}
                    className="desktop-only"
                    onClick={() => onShowMore()}
                  />
                </span>
              )}
              {order?.xxl && (
                <div
                  title={'Order is marked as XXL'}
                  className={'order-list-item__xxl'}
                >
                  <span role={'img'} aria-label={'XXL order'}>
                    !
                  </span>
                </div>
              )}
              {regionSupportsTemperatureControl(region) &&
                order?.temperatureControlled && (
                  <img
                    src={temperatureIcon}
                    alt="Temperature controlled"
                    style={{ height: 20, gridArea: 'temperature' }}
                  />
                )}
            </div>

            <div className="order-list-item__brand-icon">
              <img
                src={
                  order.brand === ServiceBrand.PORTERBUDDY
                    ? porterbuddyOrderIcon
                    : instaboxOrderIcon
                }
                alt={
                  order.brand === ServiceBrand.PORTERBUDDY
                    ? 'Porterbuddy'
                    : 'Instabox'
                }
              />
            </div>

            <div
              className="group-element"
              style={{ width: 160, textAlign: 'left' }}
            >
              <div className="strong">
                <span className="desktop-only">
                  {order.deliverFromDate.format('DD.MM')}
                  &nbsp;&nbsp;&nbsp;
                </span>
                {order.deliverFromDate.format('HH:mm')}–
                {order.deliverUntilDate.format('HH:mm')}
              </div>
              <span style={{ fontSize: '1.8rem' }}>
                {packageSizeAsText(order)} {parcelCount(order)}
              </span>
            </div>
          </div>
        </div>

        <div className="group-element max-width desktop-only">
          <span>{order.senderName}</span>
          <span
            style={
              !order.isSentFromHub()
                ? {
                    color: COLOR.PRIMARY,
                    fontSize: 18,
                    fontWeight: 650,
                  }
                : {}
            }
          >
            {order.originAddress.fullAddressString}
          </span>
        </div>

        <div className="group-element max-width desktop-only">
          <span>{order.recipientName}</span>
          <span
            style={{
              color:
                order.destinationAddress.addressValidity ===
                AddressValidity.NEEDS_MANUAL_VALIDATION
                  ? COLOR.RED
                  : order.destinationAddress.addressValidity ===
                    AddressValidity.CONFIRMED
                  ? COLOR.PRIMARY
                  : 'inherited',
            }}
          >
            {order.destinationAddress.fullAddressString}
          </span>
        </div>

        <div
          className="group-element"
          style={{ width: 150, alignItems: 'flex-end' }}
        >
          <span className="strong">{alertStatus[order.status].name}</span>
          {fetchingBundle && <Loader type="bounce" />}{' '}
        </div>
      </div>

      {!showMore && (
        <FaAngleDown
          style={{
            width: 30,
            height: 30,
            color: '#3a3d4b',
            cursor: 'pointer',
          }}
          className="desktop-only"
          onClick={() => onShowMore()}
        />
      )}

      {showMore && (
        <FaAngleUp
          style={{
            width: 30,
            height: 30,
            color: '#3a3d4b',
            cursor: 'pointer',
          }}
          className="desktop-only"
          onClick={() => onShowMore()}
        />
      )}
    </div>
  );
}
